import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { isMenteeIdentity } from "@/lib/utils/system-roles";

export const REDACTED_LABEL = "[redacted]";

export interface RedactableUser {
  id?: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  profile_picture_url?: string | null;
  redacted_fields?: string[];
}

export const buildRedactedPseudoId = (userId: string | undefined): string => {
  if (!userId) return REDACTED_LABEL;
  return `#${userId.split("-")[0]?.toUpperCase() ?? "XXXX"}`;
};

export const isFieldRedacted = (
  field: string,
  redactedFields: string[] | undefined,
  _rawValue?: unknown,
): boolean => {
  return redactedFields?.includes(field) ?? false;
};

export const inferRedactedFieldsFromNulls = (
  fields: Record<string, unknown>,
): string[] =>
  Object.entries(fields)
    .filter(([, v]) => v === null)
    .map(([k]) => k);

export const getMenteeDisplayName = (
  mentee: RedactableUser | null | undefined,
): string => {
  if (!mentee) return REDACTED_LABEL;
  if (isFieldRedacted("name", mentee.redacted_fields, mentee.name)) {
    return buildRedactedPseudoId(mentee.id);
  }
  return mentee.name?.trim() ?? "";
};

export const getPreferredName = (
  user: RedactableUser | null | undefined,
): string => {
  if (!user) return REDACTED_LABEL;
  const firstNameRedacted = isFieldRedacted(
    "first_name",
    user.redacted_fields,
    user.first_name,
  );
  const fullNameRedacted = isFieldRedacted(
    "name",
    user.redacted_fields,
    user.name,
  );
  // Either name shape can be redacted independently, so avoid partial names.
  if (firstNameRedacted || fullNameRedacted) {
    return buildRedactedPseudoId(user.id);
  }
  return user.name?.trim() || user.first_name?.trim() || "";
};

// Mentor profiles save the display name (usually a first name) into `name`,
// so non-mentee records pair it with the legal last name.
export const getRecordDisplayName = (
  user: (RedactableUser & { systemRoles?: string[] }) | null | undefined,
): string => {
  if (!user || isMenteeIdentity(user.systemRoles)) {
    return getMenteeDisplayName(user);
  }
  const preferred = user.name?.trim() || user.first_name?.trim() || "";
  const last = user.last_name?.trim() ?? "";
  if (!preferred || !last) return preferred;
  if (preferred.toLowerCase().endsWith(` ${last.toLowerCase()}`)) {
    return preferred;
  }
  return `${preferred} ${last}`;
};

export const getLegalName = (
  user: RedactableUser | null | undefined,
): string => {
  if (!user) return "";
  if (isFieldRedacted("first_name", user.redacted_fields, user.first_name)) {
    return "";
  }
  const first = user.first_name?.trim() ?? "";
  const last = isFieldRedacted(
    "last_name",
    user.redacted_fields,
    user.last_name,
  )
    ? ""
    : (user.last_name?.trim() ?? "");
  return [first, last].filter(Boolean).join(" ");
};

export const getMenteeDisplayPhoto = (
  mentee: RedactableUser | null | undefined,
): string | undefined => {
  if (!mentee) return undefined;
  if (
    isFieldRedacted(
      "profile_picture_url",
      mentee.redacted_fields,
      mentee.profile_picture_url,
    )
  ) {
    return undefined;
  }
  return mentee.profile_picture_url ?? undefined;
};

export const renderRedactableField = (
  field: string,
  rawValue: string | null | undefined,
  redactedFields: string[] | undefined,
  fallback: string = "-",
): string => {
  if (isFieldRedacted(field, redactedFields, rawValue)) return REDACTED_LABEL;
  return rawValue || fallback;
};

interface SendbirdMessageUserLike {
  user_id?: string;
  nickname?: string;
  metadata?: { role?: string };
}

export const getSendbirdMessageSenderDisplayName = (
  user: SendbirdMessageUserLike | undefined,
): string | null => {
  if (!user) return null;
  if (
    useDevOverridesStore.getState().hidePhiPii &&
    user.metadata?.role === "MENTEE"
  ) {
    return buildRedactedPseudoId(user.user_id);
  }
  if (user.nickname) return user.nickname;
  if (user.metadata?.role === "MENTEE") {
    return buildRedactedPseudoId(user.user_id);
  }
  return null;
};
